<script setup lang="ts">
import './vue-flow.scss'

import { ref } from 'vue'
import { ConnectionMode, useVueFlow, VueFlow } from '@vue-flow/core'
import type { Edge, Node } from '@vue-flow/core'
import MessageNode from './MessageNode/MessageNode.vue'
import FlatButton from '@/components/atoms/FlatButton.vue'
import MessageControlPanel from './MessageControlPanel/MessageControlPanel.vue'
import type { ScenarioChatNode, ScenarioChatForm_Message } from './types'
import ScenarioChatTitle from './ScenarioChatTitle.vue'
import { useLayout } from './useLayout'
import { Background } from '@vue-flow/background'
import StartNode from './StartNode.vue'
import IconArrowLeft from '@/assets/images/icons/arrow-left.svg?component'
import { RouterLink } from 'vue-router'
import { useInternalScenarioChatFormStore } from './form/form'
import { assertExhaustive } from '@/libs/utilities/assertExhaustive'
import { onKeyStroke } from '@vueuse/core'
import { isInputDOMNode } from './util'

const formStore = useInternalScenarioChatFormStore()

const graph = ref<[Node[], Edge[]]>(
  initializeGraph(Object.values(formStore.formValues.value.messages))
)

const vueFlow = useVueFlow()

// ページが開かれたときにノードが中心に位置するようにしたい
// 安定しないのでおまじない的に複数回呼んでいる
vueFlow.onInit(vueFlowInstance => {
  vueFlowInstance.fitView({ maxZoom: 1 })
})
vueFlow.onPaneReady(vueFlowInstance => {
  vueFlowInstance.fitView({ maxZoom: 1 })
})

const { layout } = useLayout()

async function layoutGraph(direction: 'LR' | 'TB') {
  const nodes = vueFlow.getNodes.value
  const edges = vueFlow.getEdges.value

  graph.value[0] = layout(nodes, edges, direction)
}

function initializeGraph(
  allMessages: ScenarioChatForm_Message[]
): [Node[], Edge[]] {
  const newMessageNodes = allMessages.map<ScenarioChatNode>((message, i) => {
    return {
      id: message.clientOnlyId,
      type: 'message',
      data: {
        message,
        isEntry: i === 0,
      },
      position: {
        x: 0,
        y: 0,
      },
    }
  })

  const newMessageEdges = allMessages.flatMap<Edge>(message => {
    if (message.selections.type === 'BasicSelections') {
      return message.selections.basicSelections.flatMap(selection => {
        if (selection.nextScenarioChatMessageClientOnlyId) {
          return {
            id: `${selection.clientOnlyId}-${selection.nextScenarioChatMessageClientOnlyId}`,
            source: message.clientOnlyId,
            sourceHandle: selection.clientOnlyId,
            target: selection.nextScenarioChatMessageClientOnlyId,
          }
        }

        return []
      })
    }

    return []
  })

  const startNode = {
    id: 'start',
    type: 'start',
    data: {},
    position: {
      x: 0,
      y: 0,
    },
  }

  const fromStartEdge = {
    id: 'from-start',
    source: 'start',
    target: newMessageNodes[0].id,
  }

  return [
    [startNode, ...newMessageNodes],
    [fromStartEdge, ...newMessageEdges],
  ]
}

formStore.onChange(eventGroup => {
  eventGroup.events.forEach(event => {
    switch (event.type) {
      case 'addMessage':
        const newMessageNode = {
          id: event.newMessage.clientOnlyId,
          type: 'message',
          data: {
            message: event.newMessage,
            isEntry: false,
          },
          position: {
            x: 0,
            y: 0,
          },
        }
        vueFlow.addNodes([newMessageNode])

        selectedMessageClientOnlyId.value = event.newMessage.clientOnlyId
        break
      case 'removeMessage':
        vueFlow.removeNodes([event.clientOnlyId])

        if (selectedMessageClientOnlyId.value === event.clientOnlyId) {
          selectedMessageClientOnlyId.value = undefined
        }
        break
      case 'updateMessage':
        vueFlow.updateNodeData(event.message.clientOnlyId, {
          message: event.message,
        })
        break
      case 'updateMessages':
        Object.entries(event.updates).forEach(([clientOnlyId, message]) => {
          vueFlow.updateNodeData(clientOnlyId, {
            message,
          })
        })
        break
      case 'addConnection':
        const newEdge = {
          id: `${event.sourceSelectionClientOnlyId}-${event.targetMessageClientOnlyId}`,
          source: event.sourceMessageClientOnlyId,
          sourceHandle: event.sourceSelectionClientOnlyId,
          target: event.targetMessageClientOnlyId,
        }
        vueFlow.addEdges([newEdge])
        break
      case 'removeConnection':
        const removedId = `${event.sourceSelectionClientOnlyId}-${event.targetMessageClientOnlyId}`
        vueFlow.removeEdges([removedId])
        break
      case 'removeConnections':
        event.removes.forEach(remove => {
          const removedId = `${remove.sourceSelectionClientOnlyId}-${remove.targetMessageClientOnlyId}`
          vueFlow.removeEdges([removedId])
        })
        break
      default:
        assertExhaustive(event)
    }
  })

  // Layout について、イベントをまとめて処理する
  //
  // Layout に関するイベントが連続して発生した場合に、
  // それらをまとめて一度の Layout 処理で済まして負荷の軽減を行う
  const shouldLayout = eventGroup.events.some(event => {
    return (
      event.type === 'addConnection' ||
      event.type === 'removeConnection' ||
      event.type === 'removeConnections'
    )
  })
  if (shouldLayout) {
    layoutGraph('LR')
  }
})

const selectedMessageClientOnlyId = ref<string>()

vueFlow.onNodeClick(event => {
  if (event.event.target && event.event.target instanceof HTMLElement) {
    if (event.event.target.dataset.vueflowEl === 'sourceHandle') {
      return
    }
  }

  selectedMessageClientOnlyId.value = event.node.id
})

//
// キー入力によるメッセージノードの削除
//
onKeyStroke(['Delete', 'Backspace'], e => {
  const isInputDOMNodeValue = isInputDOMNode(e)
  if (isInputDOMNodeValue) {
    return
  }

  if (!selectedMessageClientOnlyId.value) {
    return
  }

  e.preventDefault()
  const confirmed = window.confirm('メッセージを削除しますか？')
  if (!confirmed) {
    return
  }

  formStore.deleteMessage(selectedMessageClientOnlyId.value)
})

vueFlow.onConnect(params => {
  // マウス操作による Handle を使った Drag & Drop による connect 時に呼ばれる
  //
  // 内部で Edge を追加したことによって onConnect が呼ばれることは想定していないので
  // もし呼ばれていたら想定外として対応してください

  const {
    source: sourceMessageClientOnlyId,
    sourceHandle: sourceSelectionClientOnlyId,
    target: targetMessageClientOnlyId,
  } = params
  if (!sourceSelectionClientOnlyId || !targetMessageClientOnlyId) {
    return
  }
  formStore.connect({
    sourceMessageClientOnlyId,
    sourceSelectionClientOnlyId,
    targetMessageClientOnlyId,
  })
})
</script>

<template>
  <form :class="$style.container" @submit.prevent.stop="">
    <header :class="$style.header">
      <div :class="$style.headerLeft">
        <button :class="$style.backButton">
          <RouterLink :to="{ name: 'scenarioChatIndex' }">
            <IconArrowLeft :class="$style.backButton__icon" />
            <span>一覧へ戻る</span>
          </RouterLink>
        </button>
        <div :class="$style.title">
          <ScenarioChatTitle />
        </div>
      </div>
      <div>
        <FlatButton size="small" @click="formStore.submit">保存</FlatButton>
      </div>
    </header>
    <div :class="$style.containerInner">
      <VueFlow
        :nodes="graph[0]"
        :edges="graph[1]"
        :nodes-draggable="false"
        :connection-mode="ConnectionMode.Strict"
        :delete-key-code="[
          /* 無効にする。デフォルトの削除処理をコントロールするのが難しかったので、自前でやる */
        ]"
        @nodes-initialized="layoutGraph('LR')"
      >
        <template #node-start>
          <StartNode />
        </template>

        <template #node-message="props">
          <MessageNode
            :id="props.id"
            :data="props.data"
            :selected="selectedMessageClientOnlyId === props.id"
          />
        </template>

        <Background />
      </VueFlow>

      <MessageControlPanel
        v-for="message of formStore.formValues.value.messages"
        v-show="selectedMessageClientOnlyId === message.clientOnlyId"
        :key="message.clientOnlyId"
        :message="message"
        :message-client-only-id="message.clientOnlyId"
        @close="selectedMessageClientOnlyId = undefined"
        :class="$style.controlPanel"
      />
    </div>
  </form>
</template>

<style lang="scss" module>
.container {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 40px;
  background-color: #ffffff;
  border-bottom: 1px solid #e0e0e0;
}

.headerLeft {
  display: flex;
  align-items: center;
  gap: 8px;
}

.backButton {
  display: flex;

  a {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    color: $color-text-and-object-medium-emphasis;
  }

  &__icon {
    width: 18px;
    height: 18px;
    fill: $color-text-and-object-medium-emphasis;
  }
}

.title {
  width: 400px;
}

.containerInner {
  position: relative;
  width: 100%;
  height: 100%;
}

.controlPanel {
  position: absolute;
  right: 0;
  top: 0;
  width: 500px;
  height: 100%;
}
</style>
